'use client';

import { useState } from 'react';

export default function CalculatorSimpleMath() {
  const [display, setDisplay] = useState<string>('0');
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState<boolean>(false);

  const inputDigit = (digit: string) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
  };

  const clearAll = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const performOperation = (nextOperation: string) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      let newValue = currentValue;

      switch (operation) {
        case '+':
          newValue = currentValue + inputValue;
          break;
        case '-':
          newValue = currentValue - inputValue;
          break;
        case '×':
          newValue = currentValue * inputValue;
          break;
        case '÷':
          if (inputValue === 0) {
            setDisplay('Error');
            setPreviousValue(null);
            setOperation(null);
            setWaitingForOperand(true);
            return;
          }
          newValue = currentValue / inputValue;
          break;
      }

      setDisplay(String(newValue));
      setPreviousValue(newValue);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const handleEquals = () => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operation) {
      const currentValue = previousValue;
      let newValue = currentValue;

      switch (operation) {
        case '+':
          newValue = currentValue + inputValue;
          break;
        case '-':
          newValue = currentValue - inputValue;
          break;
        case '×':
          newValue = currentValue * inputValue;
          break;
        case '÷':
          if (inputValue === 0) {
            setDisplay('Error');
            setPreviousValue(null);
            setOperation(null);
            setWaitingForOperand(true);
            return;
          }
          newValue = currentValue / inputValue;
          break;
      }

      setDisplay(String(newValue));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(true);
    }
  };

  const toggleSign = () => {
    const value = parseFloat(display);
    setDisplay(String(value * -1));
  };

  const inputPercent = () => {
    const value = parseFloat(display);
    setDisplay(String(value / 100));
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto">
      <div className="space-y-4">
        {/* Display */}
        <div className="bg-slate-900 rounded-xl p-6 mb-6">
          <div className="text-right">
            <div className="text-slate-400 text-sm h-6 mb-1">
              {previousValue !== null && operation ? `${previousValue} ${operation}` : ''}
            </div>
            <div className="text-white text-5xl font-light break-all">
              {display.length > 12 ? parseFloat(display).toExponential(6) : display}
            </div>
          </div>
        </div>

        {/* Calculator Buttons */}
        <div className="grid grid-cols-4 gap-3">
          {/* Row 1: AC, C, %, ÷ */}
          <button
            onClick={clearAll}
            className="bg-slate-300 hover:bg-slate-400 text-slate-900 font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            AC
          </button>
          <button
            onClick={clear}
            className="bg-slate-300 hover:bg-slate-400 text-slate-900 font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            C
          </button>
          <button
            onClick={inputPercent}
            className="bg-slate-300 hover:bg-slate-400 text-slate-900 font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            %
          </button>
          <button
            onClick={() => performOperation('÷')}
            className={`font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95 ${
              operation === '÷' ?'bg-white text-orange-500 border-2 border-orange-500' :'bg-orange-500 hover:bg-orange-600 text-white'
            }`}
          >
            ÷
          </button>

          {/* Row 2: 7, 8, 9, × */}
          <button
            onClick={() => inputDigit('7')}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            7
          </button>
          <button
            onClick={() => inputDigit('8')}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            8
          </button>
          <button
            onClick={() => inputDigit('9')}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            9
          </button>
          <button
            onClick={() => performOperation('×')}
            className={`font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95 ${
              operation === '×' ?'bg-white text-orange-500 border-2 border-orange-500' :'bg-orange-500 hover:bg-orange-600 text-white'
            }`}
          >
            ×
          </button>

          {/* Row 3: 4, 5, 6, - */}
          <button
            onClick={() => inputDigit('4')}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            4
          </button>
          <button
            onClick={() => inputDigit('5')}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            5
          </button>
          <button
            onClick={() => inputDigit('6')}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            6
          </button>
          <button
            onClick={() => performOperation('-')}
            className={`font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95 ${
              operation === '-' ?'bg-white text-orange-500 border-2 border-orange-500' :'bg-orange-500 hover:bg-orange-600 text-white'
            }`}
          >
            −
          </button>

          {/* Row 4: 1, 2, 3, + */}
          <button
            onClick={() => inputDigit('1')}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            1
          </button>
          <button
            onClick={() => inputDigit('2')}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            2
          </button>
          <button
            onClick={() => inputDigit('3')}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            3
          </button>
          <button
            onClick={() => performOperation('+')}
            className={`font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95 ${
              operation === '+' ?'bg-white text-orange-500 border-2 border-orange-500' :'bg-orange-500 hover:bg-orange-600 text-white'
            }`}
          >
            +
          </button>

          {/* Row 5: +/-, 0, ., = */}
          <button
            onClick={toggleSign}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            +/−
          </button>
          <button
            onClick={() => inputDigit('0')}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            0
          </button>
          <button
            onClick={inputDecimal}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            .
          </button>
          <button
            onClick={handleEquals}
            className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-5 rounded-xl transition-all duration-150 active:scale-95"
          >
            =
          </button>
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-slate-50 rounded-xl">
          <p className="text-xs text-slate-600 text-center">
            <span className="font-semibold">AC</span> = Clear All • <span className="font-semibold">C</span> = Clear Display • <span className="font-semibold">%</span> = Percent • <span className="font-semibold">+/−</span> = Toggle Sign
          </p>
        </div>
      </div>
    </div>
  );
}